import { Key as KeyIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import { CommonHelper } from 'src/utils/commonHelper';
import { useTranslation } from 'react-i18next';

const OrgManageConnectedMediaComponent = ({
  handleProfileClick,
  disabledProfilesIds = [],
  handleNetworks,
  orgConnectedProfilesData = [],
  onProfileSelectionChange,
  onSocialMediaChange,
  onBrandsChange,
}) => {
  const theme = useTheme();
  const { t: translate } = useTranslation();
  const hasIntegratationPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.INTEGRATE_PERMISSION
  );

  const selectedProfiles = useMemo(
    () => orgConnectedProfilesData.filter((profile) => profile.is_selected),
    [orgConnectedProfilesData]
  );

  const orgConnectedServiceNames = useMemo(
    () => [...new Set(orgConnectedProfilesData.map((item) => item.service_name))],
    [orgConnectedProfilesData]
  );

  const selectedServiceNames = useMemo(
    () => [...new Set(selectedProfiles.map((profile) => profile.service_name))],
    [selectedProfiles]
  );

  const orgConnectedBrands = useMemo(
    () => [
      ...new Map(
        orgConnectedProfilesData.map((item) => [
          item.brand_name,
          { brand_name: item.brand_name, brand_image: item.brand_image },
        ])
      ).values(),
    ],
    [orgConnectedProfilesData]
  );

  const selectedBrands = useMemo(
    () => [
      ...new Map(
        selectedProfiles.map((profile) => [
          profile.brand_name,
          { brand_name: profile.brand_name, brand_image: profile.brand_image },
        ])
      ).values(),
    ],
    [selectedProfiles]
  );

  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const debouncedHandleProfileClick = useMemo(() => debounce(handleProfileClick, 250), [handleProfileClick]);
  return (
    <Card
      sx={{
        mx: 'auto',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: 2,
        }}
      >
        <Typography sx={{ variant: 'h6', color: 'text.primary', fontWeight: 'bold' }}>Organization Profiles</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="All profiles across social media or brands are selected." arrow>
            <Box
              sx={{
                width: 15,
                height: 15,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
          <Tooltip title="Partially selected profiles for this social media or brand." arrow>
            <Box
              sx={{
                width: 15,
                height: 15,
                borderRadius: '50%',
                bgcolor: 'warning.main',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </Box>
      </Box>
      <CardContent sx={{ pt: 2 }}>
        <Box position="relative">
          <Autocomplete
            multiple
            id="checkboxes-tags-demo-social"
            options={orgConnectedServiceNames}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {CommonHelper.GetSocialMediaAvatar(option)}
                      {CommonHelper.DisplayServiceName(option)}
                    </Box>
                    <Checkbox checked={selected} />
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedServiceNames.length === 0 && translate('common.select_social_media', 'Select Social Media')}
                label="Social Media"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const allServiceProfiles = orgConnectedProfilesData.filter(
                  (profile) => profile.service_name === option && profile.is_active
                );
                const selectedServiceProfiles = selectedProfiles.filter(
                  (profile) => profile.service_name === option && profile.is_active
                );
                const allSelected =
                  allServiceProfiles.length > 0 && allServiceProfiles.length === selectedServiceProfiles.length;
                const partiallySelected = selectedServiceProfiles.length > 0 && !allSelected;
                const chipStyles = allSelected
                  ? {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '& .MuiChip-deleteIcon': { color: theme.palette.primary.contrastText },
                    }
                  : partiallySelected
                  ? {
                      bgcolor: theme.palette.warning.main,
                      color: theme.palette.warning.contrastText,
                      '& .MuiChip-deleteIcon': { color: theme.palette.warning.contrastText },
                    }
                  : {};
                return (
                  <Chip
                    key={option}
                    avatar={CommonHelper.GetSocialMediaAvatar(option)}
                    label={CommonHelper.DisplayServiceName(option)}
                    sx={{ ...chipStyles }}
                    {...getTagProps({ index })}
                  />
                );
              })
            }
            onChange={(event, newValue) => onSocialMediaChange(newValue)}
            value={selectedServiceNames}
            sx={{ mt: 2, mb: 2 }}
          />

          <Autocomplete
            multiple
            id="checkboxes-tags-demo-brands"
            options={orgConnectedBrands}
            disableCloseOnSelect
            getOptionLabel={(option) => option.brand_name || ''}
            isOptionEqualToValue={(option, value) => option.brand_name === value.brand_name}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps} style={{ cursor: 'pointer' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex' }}>
                      <Avatar
                        src={option.brand_image || option.brand_name}
                        alt={option.brand_name}
                        sx={{ width: 24, height: 24, bgcolor: 'primary.main', mr: 1 }}
                      />
                      {option.brand_name}
                    </Box>
                    <Checkbox checked={selected} />
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder={selectedBrands.length === 0 && translate('common.select_brands', 'Select Brands')} label={translate('common.brands', 'Brands')} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const allServiceProfiles = orgConnectedProfilesData.filter(
                  (profile) => profile.brand_name === option.brand_name && profile.is_active
                );
                const selectedServiceProfiles = selectedProfiles.filter(
                  (profile) => profile.brand_name === option.brand_name && profile.is_active
                );
                const allSelected =
                  allServiceProfiles.length > 0 && allServiceProfiles.length === selectedServiceProfiles.length;
                const partiallySelected = selectedServiceProfiles.length > 0 && !allSelected;
                const chipStyles = allSelected
                  ? {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '& .MuiChip-deleteIcon': { color: theme.palette.primary.contrastText },
                    }
                  : partiallySelected
                  ? {
                      bgcolor: theme.palette.warning.main,
                      color: theme.palette.warning.contrastText,
                      '& .MuiChip-deleteIcon': { color: theme.palette.warning.contrastText },
                    }
                  : {};
                return (
                  <Chip
                    key={option.brand_name}
                    avatar={
                      <Avatar
                        src={option.brand_image || option.brand_name}
                        alt={option.brand_name}
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: 30,
                          width: 30,
                          bgcolor: 'primary.main',
                        }}
                      />
                    }
                    label={option.brand_name}
                    sx={{ ...chipStyles }}
                    {...getTagProps({ index })}
                  />
                );
              })
            }
            onChange={(event, newValue) => onBrandsChange(newValue)}
            value={selectedBrands}
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            id="org-profiles"
            options={orgConnectedProfilesData}
            disableCloseOnSelect
            getOptionLabel={(option) => option.profile_name || ''}
            groupBy={(option) => option.brand_name}
            renderGroup={(params) => {
              const brandName = params.group;
              const brandProfiles = orgConnectedProfilesData.filter((p) => p.brand_name === brandName);
              const activeBrandProfiles = brandProfiles.filter((p) => p.is_active);
              const services = [...new Set(brandProfiles.map((p) => p.service_name))];

              return (
                <li key={params.key}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    backgroundColor="primary.light"
                    sx={{ borderRadius: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1, pt: 2, pb: 2 }}>
                      <Avatar
                        src={brandProfiles[0]?.brand_image || brandProfiles[0]?.brand_name}
                        alt={brandProfiles[0]?.brand_name}
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: 30,
                          width: 30,
                          bgcolor: 'primary.main',
                        }}
                      />
                      <Typography variant="h6">{brandName}</Typography>
                    </Box>
                    <Checkbox
                      checked={
                        activeBrandProfiles.length > 0 &&
                        activeBrandProfiles.every((p) => selectedProfiles.some((s) => s.id === p.id))
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          const profilesToAdd = activeBrandProfiles.filter(
                            (p) => !selectedProfiles.some((s) => s.id === p.id) && !disabledProfilesIds.includes(p.id)
                          );
                          onProfileSelectionChange([...selectedProfiles, ...profilesToAdd]);
                        } else {
                          onProfileSelectionChange(
                            selectedProfiles.filter((p) => !brandProfiles.some((bp) => bp.id === p.id))
                          );
                        }
                      }}
                    />
                  </Box>

                  {services.map((service) => {
                    const serviceProfiles = brandProfiles.filter((p) => p.service_name === service);
                    const activeServiceProfiles = serviceProfiles.filter((p) => p.is_active);
                    const ServiceName = service?.toUpperCase();

                    return (
                      <Box key={service} sx={{ ml: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          {CommonHelper.GetServiceLabelView(ServiceName)}
                          <Checkbox
                            checked={
                              activeServiceProfiles.length > 0 &&
                              activeServiceProfiles.every((p) => selectedProfiles.some((s) => s.id === p.id))
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                const profilesToAdd = activeServiceProfiles.filter(
                                  (p) =>
                                    !selectedProfiles.some((s) => s.id === p.id) && !disabledProfilesIds.includes(p.id)
                                );
                                onProfileSelectionChange([...selectedProfiles, ...profilesToAdd]);
                              } else {
                                onProfileSelectionChange(
                                  selectedProfiles.filter((p) => !serviceProfiles.some((sp) => sp.id === p.id))
                                );
                              }
                            }}
                          />
                        </Box>

                        {serviceProfiles.map((profile) => (
                          <Box
                            key={profile.id}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              ml: 3,
                              pt: 1,
                              pb: 1,
                              cursor:
                                disabledProfilesIds.includes(profile.id) || !profile.is_active
                                  ? 'not-allowed'
                                  : 'pointer',
                              '&:hover': {
                                backgroundColor:
                                  disabledProfilesIds.includes(profile.id) || !profile.is_active
                                    ? 'transparent'
                                    : theme.palette.primary.lighter,
                              },
                              borderRadius: 1,
                            }}
                            onClick={(e) => {
                              if (
                                e.target.tagName === 'BUTTON' ||
                                e.target.tagName === 'INPUT' ||
                                disabledProfilesIds.includes(profile.id) ||
                                !profile.is_active
                              ) {
                                return;
                              }
                              if (selectedProfiles.some((s) => s.id === profile.id)) {
                                onProfileSelectionChange(selectedProfiles.filter((s) => s.id !== profile.id));
                              } else {
                                onProfileSelectionChange([...selectedProfiles, profile]);
                              }
                            }}
                          >
                            <Typography
                              style={{
                                opacity: disabledProfilesIds.includes(profile.id) || !profile.is_active ? 0.5 : 1,
                              }}
                              sx={{ pl: 1 }}
                            >
                              {CommonFunction.InitialAccountBadgeWithName(profile)}
                            </Typography>
                            {!profile.is_active && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (hasIntegratationPermission) {
                                    handleProfileClick(profile);
                                  }
                                }}
                                sx={{
                                  ml: 2,
                                  borderRadius: 1,
                                  zIndex: 2,
                                  backgroundColor: theme.palette.error.main,
                                  color: theme.palette.error.contrastText,
                                  '&:hover': {
                                    backgroundColor: theme.palette.error.dark,
                                    boxShadow: theme.shadows[3],
                                  },
                                }}
                                startIcon={<KeyIcon fontSize="small" />}
                              >
                                Re-authenticate
                              </Button>
                            )}
                            <Checkbox
                              checked={selectedProfiles.some((s) => s.id === profile.id)}
                              onChange={(e) => {
                                if (
                                  e.target.checked &&
                                  profile.is_active &&
                                  !disabledProfilesIds.includes(profile.id)
                                ) {
                                  onProfileSelectionChange([...selectedProfiles, profile]);
                                } else {
                                  onProfileSelectionChange(selectedProfiles.filter((s) => s.id !== profile.id));
                                }
                              }}
                              disabled={disabledProfilesIds.includes(profile.id) || !profile.is_active}
                            />
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                </li>
              );
            }}
            onChange={(event, newValue) => onProfileSelectionChange(newValue)}
            value={selectedProfiles}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedProfiles.length === 0 && translate('common.select_connected_profiles', 'Select Connected Profiles')}
                label="Connected Profiles"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.id}
                  avatar={
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={CommonHelper.GetSocialMediaAvatar(option.service_name)}
                      sx={{
                        '& .MuiBadge-badge': {
                          height: 20,
                          width: 20,
                          minWidth: 20,
                          bgcolor:  'white', 
                          borderRadius: '50%',
                          border: '2px solid white',
                          margin: '-4px',
                        },
                      }}
                    >
                      <Avatar
                        src={option.profile_image ? option.profile_image : option.profile_name || option.id}
                        alt={option.profile_name ? option.profile_name : option.id}
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: 30,
                          width: 30,
                          bgcolor: 'primary.main',
                        }}
                      />
                    </Badge>
                  }
                  label={option.profile_name || option.id}
                  sx={{
                    ml: 1,
                    '& .MuiChip-label': {
                      marginLeft: '6px',
                    },
                  }}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Box>
      </CardContent>
    </Card>
  );
};
export default OrgManageConnectedMediaComponent;
